import { css, cx } from "@emotion/css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { RateAnswer, renderAiText } from "src/components/AI/AIChatbot";
import { useChatStream } from "src/components/AI/hooks/useChatStream";
import { PrimaryButton } from "src/components/Default/LekUI/Button";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import TipTapEditor from "src/components/TipTapEditor/TipTapEditor";
import { ALL_TOOLBAR_FEATURES } from "src/components/TipTapEditor/TipTapToolbar";
import { markdownToHtml } from "src/helpers/markdownToHtml";
import {
	useGenerateAIQuestionDetails,
	useUpdateAiResponseContent,
} from "src/hooks/useAi";
import { getAppConfig } from "src/hooks/useAppConfig";
import { useFontSize } from "src/hooks/useFontSize";
import { useNoCopy } from "src/hooks/useNoCopy";
import { isStaff } from "src/hooks/useUser";
import StarsIcon from "src/images/Icons/newIcons/aiStar.svg";
import { PromptType } from "src/types/enums";

import { AiApprovalActions } from "./AiApprovalActions";
import { AiResponseContext } from "./AiResponseContext";
import { AiResponseReviews } from "./AiResponseReviews";
import { AiResponseVerified } from "./AiResponseVerified";

interface AiQuestionDetails {
	id: number;
	response?: string | null;
	isApproved?: boolean;
	approvedBy?: string;
	approvedAt?: string;
	likesCount?: number;
	dislikesCount?: number;
}

interface AiDiscussionProps {
	questionId: number;
	/** false = tylko zapisane omówienie; generowanie dopiero przyciskiem. */
	autoGenerate?: boolean;
}

export const AiDiscussion = ({
	questionId,
	autoGenerate = true,
}: AiDiscussionProps) => {
	const { t } = useTranslation();
	const { fontSizeClass, fontSizeNormalClass } = useFontSize();
	const { openModal } = useModals();
	const appConfig = getAppConfig();
	const { data, isLoading, refetch } = useGenerateAIQuestionDetails(
		questionId,
	) as {
		data?: AiQuestionDetails;
		isLoading: boolean;
		refetch: () => Promise<unknown>;
	};
	const { mutateAsync: updateResponse, isPending: isSaving } =
		useUpdateAiResponseContent();

	const { onCopy, noCopyClassName } = useNoCopy({
		config: appConfig.copyContentSettings.discussion,
	});

	const [isRefreshDisabled, setIsRefreshDisabled] = useState(false);
	const [refreshCooldown, setRefreshCooldown] = useState(0);
	const [editedResponse, setEditedResponse] = useState<string | null>(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isRegenerating, setIsRegenerating] = useState(false);
	const boxRef = useRef<HTMLDivElement>(null);

	const handleEdit = () => {
		setIsEditMode(true);
		setTimeout(() => {
			boxRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 40);
	};

	const handleCancelEdit = () => {
		setIsEditMode(false);
	};

	const isAdmin = isStaff();
	const displayedResponse = editedResponse ?? data?.response;
	const canEdit = isAdmin && !!data?.id && !!displayedResponse;
	const showEditor = canEdit && isEditMode;
	const editorInitialHtml = useMemo(
		() => (showEditor ? markdownToHtml(displayedResponse) : ""),
		[showEditor, displayedResponse],
	);

	const {
		messages,
		sendMessage,
		regenerate,
		setMessages,
		isSubmitted,
		isWaitingForFirstToken,
	} = useChatStream({
		onFinish: async () => {
			setIsRegenerating(false);
			await refetch();
		},
		onError: () => {
			setIsRegenerating(false);
		},
		getBody: () => ({
			questionId,
		}),
		promptType: PromptType.ANSWERS_DETAILS,
	});

	const handleSaveEdit = async (html: string) => {
		try {
			await updateResponse({ id: data.id, response: html, questionId });
			setMessages([]);
			setIsEditMode(false);
			setEditedResponse(null);
			toast.success(t("ai.edit.saveSuccess", "Zmiany zostały zapisane"), {
				position: toast.POSITION.BOTTOM_LEFT,
				autoClose: 3000,
			});
		} catch {
			toast.error(t("ai.edit.saveError", "Nie udało się zapisać zmian"), {
				position: toast.POSITION.BOTTOM_LEFT,
				autoClose: 5000,
			});
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: generowanie tylko przy montowaniu
	useEffect(() => {
		if (autoGenerate) {
			sendMessage();
		}
	}, []);

	const isMissing =
		!autoGenerate &&
		!isLoading &&
		!data?.response &&
		!isSubmitted &&
		!isWaitingForFirstToken &&
		messages.length === 0;

	const handleRefresh = useCallback(async () => {
		if (isRefreshDisabled) return;

		setMessages([]);
		await sendMessage();

		setIsRefreshDisabled(true);
		setRefreshCooldown(30);

		const timer = setInterval(() => {
			setRefreshCooldown((prev) => {
				if (prev <= 1) {
					setIsRefreshDisabled(false);
					clearInterval(timer);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, [isRefreshDisabled, sendMessage, setMessages]);

	const handleRegenerate = useCallback(() => {
		openModal(ModalsNames.RegenerateDiscussionConfirmationModal, {
			onConfirm: () => {
				setEditedResponse(null);
				setIsEditMode(false);
				setMessages([]);
				setIsRegenerating(true);
				regenerate();
			},
		});
	}, [openModal, regenerate, setMessages]);

	return (
		<>
			{data?.id && isStaff() && <AiResponseReviews responseId={data.id} />}
			{data?.id && isStaff() && <AiResponseContext responseId={data.id} />}
			<div
				ref={boxRef}
				className={cx(
					"question-discussion",
					css`
						scroll-margin-top: 100px;
					`,
				)}
			>
				{data?.isApproved && (
					<AiResponseVerified
						approvedBy={data.approvedBy}
						approvedAt={data.approvedAt}
					/>
				)}
				<div className="flex items-center gap-8">
					<div className="ai-tutor-icon">
						<StarsIcon className="icon-small" />
					</div>
					<p className={cx(fontSizeNormalClass, "m-0")}>Tutor AI</p>
				</div>

				{(messages.length === 0 || isWaitingForFirstToken) &&
					(isRegenerating ||
						((isSubmitted || isWaitingForFirstToken || isLoading) &&
							!data?.response)) && (
						<div className="w-100 flex-center-row">
							<p className="ai-tutor--generate--loader">
								{t("ai.chatbot.loader")}
								<span>.</span>
								<span>.</span>
								<span>.</span>
							</p>
						</div>
					)}

				<div
					onCopy={(e) =>
						onCopy(e, {
							questionId,
							type: "Discussion",
						})
					}
					className={cx(noCopyClassName, fontSizeClass)}
				>
					<span className="text-center text-grey size-12-light">
						{t("ai.chatbot.aiInfo")}
					</span>
					{showEditor ? (
						<TipTapEditor
							features={[...ALL_TOOLBAR_FEATURES, "table"]}
							initialHtml={editorInitialHtml}
							onSubmit={handleSaveEdit}
							onCancel={() => setIsEditMode(false)}
							submitText={
								isSaving
									? t("ai.edit.saving", "Zapisywanie...")
									: t("ai.edit.save", "Zapisz")
							}
							cancelText={t("ai.edit.cancel", "Anuluj")}
						/>
					) : (
						<>
							{messages.length > 0 && !isWaitingForFirstToken
								? messages.map(({ parts }, messageIndex) => (
										<div key={messageIndex}>
											{parts?.map((part, partIndex) =>
												part.type === "text" ? (
													<div key={partIndex}>{renderAiText(part.text)}</div>
												) : null,
											)}
										</div>
									))
								: !isRegenerating &&
									displayedResponse && (
										<div>{renderAiText(displayedResponse)}</div>
									)}

							{isMissing && (
								<div className="flex flex-col items-start gap-12">
									<span className="size-14-light text-grey">
										{t(
											"ai.discussion.missing",
											"Brak omówienia AI dla tego pytania.",
										)}
									</span>
									<PrimaryButton
										onClick={() => sendMessage()}
										data-test-id="ai-discussion-generate"
									>
										{t("ai.discussion.generate", "Wygeneruj omówienie AI")}
									</PrimaryButton>
								</div>
							)}
							{!isMissing && data?.id && !data.response && (
								<div>
									<PrimaryButton
										disabled={isRefreshDisabled}
										onClick={handleRefresh}
									>
										{isRefreshDisabled
											? `${t("commands.refresh")} (${refreshCooldown}s)`
											: `${t("commands.refresh")}`}
									</PrimaryButton>
								</div>
							)}
						</>
					)}

					{data?.id && data.response && !isRegenerating && (
						<div className="flex flex-col gap-12">
							<span className="divider divider--horizontal" />
							<RateAnswer
								responseId={data.id}
								likesCount={data.likesCount}
								dislikesCount={data.dislikesCount}
							/>
							<span className="text-center text-grey size-12-light">
								{t("ai.chatbot.aiInfo")}
							</span>
						</div>
					)}
				</div>
			</div>
			{data?.id && data.response && !isRegenerating && (
				<div className="flex justify-end">
					<AiApprovalActions
						responseId={data.id}
						questionId={questionId}
						isApproved={data.isApproved}
						isEditMode={isEditMode}
						onEdit={canEdit ? handleEdit : undefined}
						onCancelEdit={canEdit ? handleCancelEdit : undefined}
						onRegenerate={isAdmin ? handleRegenerate : undefined}
					/>
				</div>
			)}
		</>
	);
};
